import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminApi } from '@vmgate/common';
import type { CustomerListResponse, CustomerSummary, PortalAccountResponse } from '@vmgate/common';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { ErrorState } from '../components/common/ErrorState';
import { PaginationControls } from '../components/common/PaginationControls';
import { StatusBadge } from '../components/common/StatusBadge';
import { CustomerForm } from '../components/customers/CustomerForm';
import { formatDateTime, formatNumber } from '../utils/format';

export function CustomersPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSummary | null>(null);
  const [mode, setMode] = useState<'create' | 'view' | 'edit'>('create');
  const [pendingCustomerId, setPendingCustomerId] = useState<string | null>(null);

const customersQuery = useQuery<CustomerListResponse>({
    queryKey: ['customers', page, searchTerm],
    queryFn: () => adminApi.getCustomers({ page, search: searchTerm || undefined }),
    placeholderData: (previousData) => previousData
  });
  const { data, isLoading, isError, refetch, isFetching } = customersQuery;

  const rotateTokenMutation = useMutation<void, Error, CustomerSummary>({
    mutationFn: async (customer: CustomerSummary) => {
      await adminApi.updateCustomer(customer.customer_id, { rotate_token: true });
    },
    onSuccess: () => {
      refetch();
    }
  });

  useEffect(() => {
    if (!selectedCustomer || !data) return;
    const updated = data.customers.find((item) => item.customer_id === selectedCustomer.customer_id);
    if (updated) {
      setSelectedCustomer(updated);
    }
  }, [data, selectedCustomer?.customer_id]);

  useEffect(() => {
    if (!pendingCustomerId || !data) return;
    const created = data.customers.find((item) => item.customer_id === pendingCustomerId);
    if (created) {
      setSelectedCustomer(created);
      setMode('view');
      setPendingCustomerId(null);
    }
  }, [data, pendingCustomerId]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleSelectCustomer = (customer: CustomerSummary) => {
    setSelectedCustomer(customer);
    setMode('view');
  };

  const handleCreateMode = () => {
    setSelectedCustomer(null);
    setMode('create');
  };

  const handleEditMode = () => {
    if (selectedCustomer) {
      setMode('edit');
    }
  };

  const customers = data?.customers ?? [];
  const totalPages = data?.pages ?? 1;

  const summaryCards = useMemo(() => {
    const totalQuota = data?.customers.reduce((acc, item) => acc + item.license_quota_total, 0) ?? 0;
    const totalAssigned = data?.customers.reduce((acc, item) => acc + item.license_quota_assigned, 0) ?? 0;
    return { totalQuota, totalAssigned };
  }, [data]);

  const handleRotateToken = async (customer: CustomerSummary) => {
    await rotateTokenMutation.mutateAsync(customer);
  };

  if (isLoading) {
    return <LoadingScreen message="Loading customers..." />;
  }

  if (isError || !data) {
    return <ErrorState onRetry={() => refetch()} message="Unable to load customers." />;
  }

  return (
    <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: '2fr 1.1fr', alignItems: 'start' }}>
      <div className="surface surface-padding" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>Customers</h2>
            <div className="text-muted">Manage clients, license quotas, and deployment tokens.</div>
          </div>
          <button className="btn btn-primary" type="button" onClick={handleCreateMode}>
            + New customer
          </button>
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem'
          }}
        >
          <div className="surface" style={miniCardStyle}>
            <div className="text-muted">Customers</div>
            <div style={miniCardValue}>{formatNumber(data.total)}</div>
          </div>
          <div className="surface" style={miniCardStyle}>
            <div className="text-muted">Quota Total</div>
            <div style={miniCardValue}>{formatNumber(summaryCards.totalQuota)}</div>
          </div>
          <div className="surface" style={miniCardStyle}>
            <div className="text-muted">Quota Assigned</div>
            <div style={miniCardValue}>{formatNumber(summaryCards.totalAssigned)}</div>
          </div>
        </div>

        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Search company, customer ID, email..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            style={{ ...inputStyle, flex: '1 1 260px' }}
          />
          <button type="submit" className="btn btn-primary" disabled={isFetching}>
            Search
          </button>
          {searchTerm && (
            <button type="button" className="btn" onClick={handleClearSearch}>
              Clear
            </button>
          )}
        </form>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Customer ID</th>
                <th>Quota</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => {
                const quotaLabel = `${customer.license_quota_assigned}/${customer.license_quota_total}`;
                return (
                  <tr
                    key={customer.customer_id}
                    onClick={() => handleSelectCustomer(customer)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div style={{ fontWeight: 600 }}>{customer.company_name}</div>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                        {customer.contact_email}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace' }}>{customer.customer_id}</td>
                    <td>{quotaLabel}</td>
                    <td>
                      <StatusBadge status={customer.status} />
                    </td>
                    <td>{formatDateTime(customer.created_at)}</td>
                  </tr>
                );
              })}
              {customers.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted" style={{ textAlign: 'center', padding: '1rem' }}>
                    No customers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <PaginationControls page={data.current_page} totalPages={totalPages} onPageChange={setPage} disabled={isFetching} />
      </div>

      <div className="surface surface-padding" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {mode === 'create' && (
          <>
            <h3 style={{ margin: 0 }}>Create customer</h3>
            <CustomerForm
              mode="create"
              customer={null}
              onSuccess={(result) => {
                if (result?.customer_id) {
                  setPendingCustomerId(result.customer_id);
                  if (page !== 1) {
                    setPage(1);
                  }
                }
                setMode('view');
                refetch();
              }}
            />
          </>
        )}

        {mode === 'view' && selectedCustomer && (
          <CustomerDetails
            customer={selectedCustomer}
            onEdit={handleEditMode}
            onRotateToken={() => handleRotateToken(selectedCustomer)}
            rotating={rotateTokenMutation.isPending}
            onRefresh={refetch}
          />
        )}

        {mode === 'edit' && selectedCustomer && (
          <>
            <h3 style={{ margin: 0 }}>Update customer</h3>
            <CustomerForm
              mode="edit"
              customer={selectedCustomer}
              onSuccess={() => {
                setMode('view');
                refetch();
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

function CustomerDetails({
  customer,
  onEdit,
  onRotateToken,
  rotating,
  onRefresh
}: {
  customer: CustomerSummary;
  onEdit: () => void;
  onRotateToken: () => void | Promise<void>;
  rotating: boolean;
  onRefresh: () => void;
}) {
  const [newPassword, setNewPassword] = useState('');
  const [portalStatus, setPortalStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [portalMessage, setPortalMessage] = useState<string | null>(null);

  const portalAccountQuery = useQuery<PortalAccountResponse>({
    queryKey: ['customer-portal-account', customer.customer_id],
    queryFn: () => adminApi.getCustomerPortalAccount(customer.customer_id),
    staleTime: 0
  });

  useEffect(() => {
    const account = portalAccountQuery.data?.account;
    if (account) {
      setPortalStatus(account.status);
    } else {
      setPortalStatus('ACTIVE');
    }
  }, [portalAccountQuery.data?.account?.status]);

  const updatePortalAccountMutation = useMutation({
    mutationFn: (payload: { password?: string; status?: 'ACTIVE' | 'INACTIVE' }) =>
      adminApi.updateCustomerPortalAccount(customer.customer_id, payload),
    onSuccess: () => {
      setNewPassword('');
      setPortalMessage('Portal account updated.');
      portalAccountQuery.refetch();
    },
    onError: (error: unknown) => {
      setPortalMessage(error instanceof Error ? error.message : 'Unable to update portal account.');
    }
  });

  const handlePortalSubmit = (event: FormEvent) => {
    event.preventDefault();
    setPortalMessage(null);
    if (!portalAccountQuery.data?.exists && !newPassword.trim()) {
      setPortalMessage('Password required to create a portal account.');
      return;
    }
    const payload: { password?: string; status?: 'ACTIVE' | 'INACTIVE' } = {};
    if (newPassword.trim()) {
      payload.password = newPassword.trim();
    }
    if (portalStatus) {
      payload.status = portalStatus;
    }
    updatePortalAccountMutation.mutate(payload);
  };

  const renderPortalSection = () => {
    if (portalAccountQuery.isLoading) {
      return <div className="text-muted">Loading portal account…</div>;
    }
    if (portalAccountQuery.isError) {
      return <div style={{ color: 'var(--color-danger)' }}>Unable to load portal account.</div>;
    }

    const exists = portalAccountQuery.data?.exists;
    const account = portalAccountQuery.data?.account;
    const displayEmail =
      account?.email || portalAccountQuery.data?.suggested_email || customer.contact_email || 'Not set';

    return (
      <form onSubmit={handlePortalSubmit} style={{ display: 'grid', gap: '0.75rem' }}>
        <div>
          <div style={{ fontWeight: 600 }}>Portal email</div>
          <div>{displayEmail}</div>
        </div>
        {exists && account && (
          <div>
            <div style={{ fontWeight: 600 }}>Current password (hashed)</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{account.password_hash}</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              Stored as hash; enter a new password below to replace it.
            </div>
          </div>
        )}
        {!exists && (
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            No portal account exists yet. Set a password below to create one automatically.
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          <span style={{ fontWeight: 600 }}>New password</span>
          <input
            type="text"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            placeholder="Leave blank to keep current"
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          <span style={{ fontWeight: 600 }}>Status</span>
          <select value={portalStatus} onChange={(event) => setPortalStatus(event.target.value as 'ACTIVE' | 'INACTIVE')} style={inputStyle}>
            <option value="ACTIVE">ACTIVE</option>
            <option value="INACTIVE">INACTIVE</option>
          </select>
        </label>
        {portalMessage && (
          <div className="text-muted" style={{ color: portalMessage.includes('Unable') ? 'var(--color-danger)' : '#047857' }}>
            {portalMessage}
          </div>
        )}
        <button className="btn btn-primary" type="submit" disabled={updatePortalAccountMutation.isPending}>
          {updatePortalAccountMutation.isPending ? 'Saving…' : 'Save portal account'}
        </button>
      </form>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <h3 style={{ margin: '0 0 0.25rem 0' }}>{customer.company_name}</h3>
        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
          {customer.contact_email}
        </div>
      </div>

      <div className="surface" style={{ borderRadius: '12px', padding: '1rem', background: 'rgba(15, 23, 42, 0.04)' }}>
        <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--color-muted)', marginBottom: '0.4rem' }}>
          Deployment token
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>{customer.deployment_token}</div>
      </div>

      <div className="action-buttons">
        <button className="btn btn-primary" type="button" onClick={onEdit}>
          Edit details
        </button>
        <button className="btn" type="button" onClick={onRefresh}>
          Refresh
        </button>
        <button className="btn btn-danger" type="button" onClick={onRotateToken} disabled={rotating}>
          {rotating ? 'Rotating...' : 'Rotate token'}
        </button>
      </div>

      <table className="data-table" style={{ marginTop: '1rem' }}>
        <tbody>
          <tr>
            <th style={{ width: '40%' }}>Customer ID</th>
            <td>{customer.customer_id}</td>
          </tr>
          <tr>
            <th>Contact phone</th>
            <td>{customer.contact_phone || '—'}</td>
          </tr>
          <tr>
            <th>Address</th>
            <td>{customer.address || '—'}</td>
          </tr>
          <tr>
            <th>Status</th>
            <td>
              <StatusBadge status={customer.status} />
            </td>
          </tr>
          <tr>
            <th>Installation</th>
            <td>{customer.installation_service.replace(/_/g, ' ')}</td>
          </tr>
          <tr>
            <th>Support contract</th>
            <td>{customer.support_contract_status.replace(/_/g, ' ')}</td>
          </tr>
          <tr>
            <th>Licenses</th>
            <td>
              {customer.license_quota_assigned}/{customer.license_quota_total} assigned ({customer.license_quota_available} available)
            </td>
          </tr>
          <tr>
            <th>Notes</th>
            <td>{customer.notes || '—'}</td>
          </tr>
          <tr>
            <th>Created</th>
            <td>{formatDateTime(customer.created_at)}</td>
          </tr>
          <tr>
            <th>Updated</th>
            <td>{formatDateTime(customer.updated_at)}</td>
          </tr>
        </tbody>
      </table>

      <div className="surface" style={{ padding: '1rem', display: 'grid', gap: '0.75rem' }}>
        <h4 style={{ margin: 0 }}>Portal account</h4>
        {renderPortalSection()}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: '10px',
  padding: '0.65rem 0.85rem',
  fontSize: '0.95rem',
  background: '#fff'
};

const miniCardStyle: React.CSSProperties = {
  padding: '1rem',
  borderRadius: '12px',
  border: '1px solid var(--color-border)'
};

const miniCardValue: React.CSSProperties = {
  fontSize: '1.4rem',
  fontWeight: 600
};

